<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'

import useCountries from '@/composables/countries'
import type { AudienceRule, Country, CustomerTag, Survey, ProductWithPublicId } from '@/types/types'
import type { RulePrimitive } from '@/types/types'
import type { Contest } from '@/views/contests/Components/types/global'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import { capitalize } from '@/utils/string'

const { formatFromArrayToDatePicker } = useDate()
const { gendersList } = useCustomer()
const { isRuleValueValid, isAudienceRuleWithParameters, determineRuleType } = useAudience()
const { t } = useI18n()

const props = defineProps<{
  rules: Record<string, AudienceRule>
}>()
const games = inject<Contest[]>('games', [])
const selectedStores = inject<{ id: number; name: string }[]>('selectedStores', [])
const surveys = inject<Survey[]>('surveys', [])
const marketingTags = inject<CustomerTag[]>('marketingTags', [])
const channelTags = inject<CustomerTag[]>('channelTags', [])

const selectedProducts = ref<ProductWithPublicId[]>([])
const isLoadingProducts = ref(false)

const { countries: allCountries, countriesError, fetchCountries } = useCountries()
const countryById = computed<Map<number, Country>>(
  () => new Map(allCountries.value.filter((c) => c.id != null).map((c) => [c.id as number, c]))
)
const countryByAlpha2Code = computed<Map<string, Country>>(
  () => new Map(allCountries.value.filter((c) => c.alpha2Code).map((c) => [String(c.alpha2Code).toUpperCase(), c]))
)

const countryRuleValues = computed<Array<string | number | null>>(() => {
  return getRuleValues('country').filter(
    (value): value is string | number | null => typeof value === 'string' || typeof value === 'number' || value === null
  )
})

const selectedCountryNames = computed<string[]>(() =>
  countryRuleValues.value
    .filter((v): v is string | number => v !== null)
    .map((countryValue) => {
      if (typeof countryValue === 'string') {
        const alpha2Code = countryValue.toUpperCase()
        return countryByAlpha2Code.value.get(alpha2Code)?.name ?? alpha2Code
      }
      return countryById.value.get(countryValue)?.name ?? String(countryValue)
    })
)

const hasUnknownCountry = computed<boolean>(() => countryRuleValues.value.includes(null))
const hasSelectedCountries = computed<boolean>(() => countryRuleValues.value.some((value) => value !== null))
const retryFetchCountries = () => fetchCountries(true)
const getRuleValues = (ruleKey: string): RulePrimitive[] => {
  const value = props.rules[ruleKey]?.value
  if (Array.isArray(value)) return value
  return value === undefined ? [] : [value]
}
const hasRuleValues = (ruleKey: string) => getRuleValues(ruleKey).length > 0
const getFirstNumberValue = (ruleKey: string): number | null => {
  const firstValue = getRuleValues(ruleKey)[0]
  return typeof firstValue === 'number' ? firstValue : null
}

const genderText = (slug: string | null): string | undefined => gendersList.find((g) => g.slug === slug)?.text

const genderDisplayNames = computed(() =>
  getRuleValues('gender').map((slug) => {
    if (slug === null) return genderText(null) ?? ''
    if (typeof slug === 'string') return genderText(slug) ?? slug
    return String(slug)
  })
)

const channelTagIds = computed<Set<number>>(() => new Set(channelTags.map((c) => c.id)))
const channelTagById = computed<Map<number, CustomerTag>>(() => new Map(channelTags.map((c) => [c.id, c])))
const marketingTagIds = computed<Set<number>>(() => new Set(marketingTags.map((m) => m.id)))
const marketingTagById = computed<Map<number, CustomerTag>>(() => new Map(marketingTags.map((m) => [m.id, m])))

const numericTagIds = computed<number[]>(() =>
  getRuleValues('tags').filter((id): id is number => typeof id === 'number')
)

const hasAtLeastOneRuleSet = (keys: string[]) => {
  const blockRules = Object.entries(props.rules)
    .filter((record) => keys.includes(record[0]))
    .map(([, rule]) => rule) as unknown

  return (blockRules as AudienceRule[]).some((rule) => {
    return isRuleValueValid(rule.value)
  })
}

const hasCountryBlock = computed(() => hasAtLeastOneRuleSet(['country']))
const hasVisitBlock = computed(() =>
  hasAtLeastOneRuleSet(['lastValidCheckin', 'registrationDate', 'checkinCount', 'averageCheckinCount'])
)
const hasEngagementBlock = computed(() =>
  hasAtLeastOneRuleSet(['pointTotal', 'pointCumulated', 'canReceivePushNotifications'])
)

const hasContestGameParticipationBlock = computed(() => hasAtLeastOneRuleSet(['contestGameParticipation']))
const hasSurveyBlock = computed(() => hasAtLeastOneRuleSet(['surveys']))
const hasConsumedProductsBlock = computed(() => hasAtLeastOneRuleSet(['consumedProduct']))
const hasWebappLoginRule = computed(() => hasAtLeastOneRuleSet(['webappLogIn']))

const allGenders = computed(() => {
  const genderRules = getRuleValues('gender')
  return genderRules.length === 0 || genderRules.length === gendersList.length
})

const getTagName = (operator: string, ruleValue: RulePrimitive | RulePrimitive[], isDate = false): string => {
  if (operator === 'between') {
    const [min, max] = ruleValue as string[]
    if (typeof ruleValue === 'string' && dayjs(min).isValid()) {
      const minDate = dayjs(min, 'YYYY-MM-DD').format('DD/MM/YYYY')
      const maxDate = dayjs(max, 'YYYY-MM-DD').format('DD/MM/YYYY')
      return t('pages.audience.operators.tagBetween', { min: minDate, max: maxDate })
    }
    return t('pages.audience.operators.tagBetween', { min, max })
  }
  if (isDate) {
    return `${capitalize(t(`pages.audience.operators.date.${operator}`))} ${formatFromArrayToDatePicker(ruleValue as string[])}`
  }
  return `${capitalize(t(`pages.audience.operators.${operator}`))} ${ruleValue}`
}

const getRelativeDateTag = (operator: string, ruleValue: string[]): string => {
  if (determineRuleType(ruleValue) === 'absolute') {
    if (operator === 'between') {
      const [min, max] = ruleValue as string[]
      if (dayjs(min).isValid()) {
        const minDate = dayjs(min, 'YYYY-MM-DD').format('DD/MM/YYYY')
        const maxDate = dayjs(max, 'YYYY-MM-DD').format('DD/MM/YYYY')
        return t('pages.audience.operators.tagBetween', { min: minDate, max: maxDate })
      }
    }
    return `${capitalize(t(`pages.audience.operators.date.${operator}`))} ${formatFromArrayToDatePicker(ruleValue)}`
  }
  return `${capitalize(t(`pages.audience.operators.date.relatives.${operator}`))} ${ruleValue} ${t('global.words.days')}`
}

const OPERATOR_TYPES = {
  NOT_IN: 'ni',
  IN: 'in'
} as const

const QUANTITY_OPERATORS = {
  EQUAL: 'eq',
  GREATER_THAN_OR_EQUAL: 'gte',
  LESS_THAN_OR_EQUAL: 'lte',
  BETWEEN: 'between'
} as const

const createTagComputed = (ruleKey: string, isDate = false) => {
  return computed(() => {
    const rule = props.rules[ruleKey]
    if (!rule?.operator || !rule?.value) return ''

    return isDate
      ? getRelativeDateTag(rule.operator, rule.value as string[])
      : getTagName(rule.operator, rule.value, isDate)
  })
}

const createDateFilterComputed = (ruleKey: string) => {
  return computed(() => {
    const rule = props.rules[ruleKey]
    if (!rule || !isAudienceRuleWithParameters(rule) || !isRuleValueValid(rule?.parameters?.date_filter?.value)) {
      return ''
    }
    return getRelativeDateTag(rule.parameters.date_filter.operator, rule.parameters.date_filter.value as string[])
  })
}

const ageTag = createTagComputed('age')
const pointTotalTag = createTagComputed('pointTotal')
const pointCumulatedTag = createTagComputed('pointCumulated')
const lastValidCheckinTag = createTagComputed('lastValidCheckin', true)
const registrationDateTag = createTagComputed('registrationDate', true)
const checkinCountTag = createTagComputed('checkinCount')
const averageCheckinCountTag = createTagComputed('averageCheckinCount')

const checkinCountDateFilterTag = createDateFilterComputed('checkinCount')
const averageCheckinCountDateFilterTag = createDateFilterComputed('averageCheckinCount')
const lastValidCheckinDateFilterTag = createDateFilterComputed('lastValidCheckin')
const registrationDateDateFilterTag = createDateFilterComputed('registrationDate')
const pointCumulatedDateFilterTag = createDateFilterComputed('pointCumulated')
const consumedProductDateFilterTag = createDateFilterComputed('consumedProduct')

const hasAgeRule = computed(() => {
  const age = getFirstNumberValue('age')
  return age !== null && age >= 0
})
const hasOptinRule = computed(() => hasRuleValues('optin'))
const hasPointTotalRule = computed(() => {
  const pointTotal = getFirstNumberValue('pointTotal')
  return pointTotal !== null && pointTotal >= 0
})
const hasPointCumulatedRule = computed(() => isRuleValueValid(props.rules['pointCumulated']?.value))
const hasConsumedProductRule = computed(() => hasRuleValues('consumedProduct'))

const consumedProductRule = computed(() => props.rules['consumedProduct'])

const consumedProductLabel = computed(() => {
  const rule = consumedProductRule.value
  if (!rule) return ''
  return rule.operator === OPERATOR_TYPES.NOT_IN
    ? t('pages.audience.cards.notConsumedProducts')
    : t('pages.audience.cards.consumedProducts')
})

const consumedProductQuantityInfo = computed(() => {
  const rule = consumedProductRule.value
  if (!rule || !isAudienceRuleWithParameters(rule) || !rule?.parameters?.quantity_filter?.value) return ''

  const operator = rule.parameters.quantity_filter.operator
  const value = rule.parameters.quantity_filter.value

  if (operator === QUANTITY_OPERATORS.BETWEEN && Array.isArray(value) && value.length >= 2) {
    return `- ${t('pages.audience.operators.between')} ${value[0]} ${t('global.words.and')} ${value[1]} ${t('global.words.times')}`
  }

  if (typeof value === 'number' && value >= 1) {
    let operatorText = ''
    switch (operator) {
      case QUANTITY_OPERATORS.EQUAL:
        operatorText = capitalize(t('pages.audience.operators.feminine.eq'))
        break
      case QUANTITY_OPERATORS.GREATER_THAN_OR_EQUAL:
        operatorText = capitalize(t('pages.audience.operators.quantity.atLeast'))
        break
      case QUANTITY_OPERATORS.LESS_THAN_OR_EQUAL:
        operatorText = capitalize(t('pages.audience.operators.quantity.atMost'))
        break
      default:
        return ''
    }
    return `- ${operatorText} ${value} ${t('global.words.times')}`
  }

  return ''
})

const hasConsumedProductQuantityFilter = computed(() => {
  const rule = consumedProductRule.value
  if (!rule || !isAudienceRuleWithParameters(rule)) return false

  const value = rule?.parameters?.quantity_filter?.value
  if (Array.isArray(value)) {
    return value.length >= 2 && value.every((v) => typeof v === 'number' && v >= 1)
  }

  return typeof value === 'number' && value >= 1
})

const hasConsumedProductDateFilter = computed(() => {
  const rule = consumedProductRule.value
  return rule && isAudienceRuleWithParameters(rule) && isRuleValueValid(rule?.parameters?.date_filter?.value)
})

const visitItems = ['lastValidCheckin', 'registrationDate'] as const
const countItems = ['checkinCount', 'averageCheckinCount'] as const

const hasSelectedChannelTags = computed(() => numericTagIds.value.some((tagId) => channelTagIds.value.has(tagId)))

const hasSelectedMarketingTags = computed(() => numericTagIds.value.some((tagId) => marketingTagIds.value.has(tagId)))

const hasVisitBlockWithTags = computed(() => hasVisitBlock.value || hasSelectedChannelTags.value)

const hasEngagementBlockWithTags = computed(() => hasEngagementBlock.value || hasSelectedMarketingTags.value)

const classes: {
  blockContainer: string
  blockTitleContainer: string
  blockTitle: string
  fieldsetContainer: string
  label: string
  tagsContainer: string
  tagsColors: ChipColor
} = {
  blockContainer: '',
  blockTitleContainer: 'inline-flex items-start justify-start gap-2.5 self-stretch',
  blockTitle: 'shrink grow basis-0 text-base font-semibold leading-normal text-foreground',
  fieldsetContainer: 'mt-3 flex flex-col items-start justify-start gap-2 self-stretch',
  label: 'text-xs font-normal leading-tight text-foreground-secondary',
  tagsContainer: 'flex flex-wrap items-center justify-start gap-3',
  tagsColors: 'slate'
}

const surveyBlock = ref<HTMLDivElement | null>(null)

const fetchSelectedProducts = async (productPublicIds: string[]) => {
  if (!productPublicIds || productPublicIds.length === 0) {
    selectedProducts.value = []
    return
  }

  isLoadingProducts.value = true
  try {
    const response = await axios.get('/products', {
      params: {
        publicIds: productPublicIds.filter((id) => id !== null),
        includeMenus: 1,
        light: 1,
        paginated: 0
      }
    })
    selectedProducts.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching selected products:', error)
    selectedProducts.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

watch(
  () => props.rules['country']?.value,
  async (newValue) => {
    if (Array.isArray(newValue) && newValue.some((v) => v !== null)) {
      await fetchCountries()
    }
  },
  { immediate: true }
)

watch(
  () => props.rules['consumedProduct']?.value,
  async (newProductPublicIds) => {
    if (Array.isArray(newProductPublicIds) && newProductPublicIds.length > 0) {
      const productIds = newProductPublicIds.filter((id): id is string => typeof id === 'string')
      await fetchSelectedProducts(productIds)
    } else {
      selectedProducts.value = []
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="flex flex-col gap-5">
    <div :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.profil.title') }}
        </div>
      </div>
      <div :class="classes.fieldsetContainer">
        <div :class="classes.label">{{ t('pages.audience.audienceCreatePage.block.profil.store.label') }}</div>
        <div :class="classes.tagsContainer">
          <BaseChip
            v-if="!selectedStores.length"
            color="slate"
            :text="t('pages.audience.audienceCreatePage.block.profil.store.all')"
          />
          <BaseChip v-for="store in selectedStores" v-else :key="store.id" color="slate" :text="store.name" />
        </div>
      </div>
      <div v-if="hasCountryBlock" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.profil.country.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <span :class="classes.label">
            {{
              rules['country']?.operator === 'ni' ? t('pages.audience.operators.ni') : t('pages.audience.operators.in')
            }}
          </span>
          <BaseChip v-for="name in selectedCountryNames" :key="name" :text="name" color="slate" />
          <BaseChip
            v-if="hasUnknownCountry"
            :text="t('pages.audience.audienceCreatePage.block.profil.country.unknown')"
            color="slate"
          />
        </div>
        <div v-if="countriesError && hasSelectedCountries" class="text-xs font-medium text-amber-600">
          {{ t('notifications.save.error') }}
          <button type="button" class="ml-2 text-primary hover:underline" @click="retryFetchCountries">
            {{ t('global.words.refresh') }}
          </button>
        </div>
      </div>
      <div v-if="allGenders || genderDisplayNames.length" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.profil.genders.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip
            v-if="allGenders"
            :text="t('pages.audience.audienceCreatePage.block.profil.genders.all')"
            color="slate"
          />
          <template v-for="displayName in genderDisplayNames" v-else :key="displayName">
            <BaseChip :text="displayName" color="slate" />
          </template>
        </div>
      </div>
      <div v-if="hasAgeRule || hasRuleValues('birthDate')" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.profil.age.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip v-if="hasAgeRule" color="slate" :text="ageTag" />
          <BaseChip
            v-if="hasRuleValues('birthDate')"
            color="slate"
            :text="
              rules['birthDate']?.operator === 'exist'
                ? t('pages.audience.audienceCreatePage.block.profil.presence.filled')
                : t('pages.audience.audienceCreatePage.block.profil.presence.empty')
            "
          />
        </div>
      </div>
      <template v-for="presenceKey in ['firstName', 'lastName', 'email'] as const" :key="presenceKey">
        <div v-if="hasRuleValues(presenceKey)" :class="classes.fieldsetContainer">
          <div :class="classes.label">
            {{ t(`pages.audience.audienceCreatePage.block.profil.${presenceKey}.label`) }}
          </div>
          <div :class="classes.tagsContainer">
            <BaseChip
              color="slate"
              :text="
                rules[presenceKey]?.operator === 'exist'
                  ? t('pages.audience.audienceCreatePage.block.profil.presence.filled')
                  : t('pages.audience.audienceCreatePage.block.profil.presence.empty')
              "
            />
          </div>
        </div>
      </template>
      <div v-if="hasOptinRule" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.profil.optin.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <template v-for="optin in getRuleValues('optin')" :key="optin">
            <BaseChip :text="t('pages.audience.audienceCreatePage.block.profil.optin.' + optin)" color="slate" />
          </template>
        </div>
      </div>
      <div v-if="hasRuleValues('hasOptinEmail')" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.profil.hasOptinEmail.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip
            color="slate"
            :text="t('pages.audience.audienceCreatePage.block.profil.optin.' + getRuleValues('hasOptinEmail')[0])"
          />
        </div>
      </div>
    </div>
    <div
      v-if="
        hasConsumedProductsBlock ||
        (hasRuleValues('consumedProduct') && isAudienceRuleWithParameters(rules['consumedProduct']))
      "
      :class="classes.blockContainer"
    >
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.cards.consumption') }}
        </div>
      </div>
      <div v-if="hasConsumedProductRule" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ consumedProductLabel }}
          <span v-if="hasConsumedProductQuantityFilter">
            <span>{{ consumedProductQuantityInfo }}</span>
          </span>
          <span v-if="hasConsumedProductDateFilter">
            <span>- {{ consumedProductDateFilterTag }}</span>
          </span>
        </div>
        <div class="flex flex-wrap gap-3">
          <template v-if="isLoadingProducts">
            <AppSkeleton v-for="i in 2" :key="i" type="text" class="mt-1" />
          </template>
          <template v-else>
            <BaseChip v-for="product in selectedProducts" :key="product.id" :text="product.name" color="slate" />
          </template>
        </div>
      </div>
    </div>
    <div v-if="hasRuleValues('city')" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.profil.city.label') }}
        </div>
      </div>
      <div :class="classes.fieldsetContainer">
        <div :class="classes.tagsContainer">
          <BaseChip
            color="slate"
            :text="
              rules['city']?.operator === 'exist'
                ? t('pages.audience.audienceCreatePage.block.profil.presence.filled')
                : t('pages.audience.audienceCreatePage.block.profil.presence.empty')
            "
          />
        </div>
      </div>
    </div>
    <div v-if="hasVisitBlockWithTags" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.visit.title') }}
        </div>
      </div>
      <div v-for="item in visitItems" :key="item" class="space-y-3">
        <div
          v-if="isRuleValueValid(rules[item]?.value) || isRuleValueValid(rules[item]?.parameters?.date_filter?.value)"
          :class="classes.fieldsetContainer"
        >
          <div :class="classes.label">
            {{ t('pages.audience.audienceCreatePage.block.visit.' + item + '.label') }}
          </div>
          <div :class="classes.tagsContainer">
            <BaseChip
              color="slate"
              :text="
                (item === 'lastValidCheckin' ? lastValidCheckinTag : registrationDateTag) ||
                (item === 'lastValidCheckin' ? lastValidCheckinDateFilterTag : registrationDateDateFilterTag)
              "
            />
          </div>
        </div>
      </div>
      <div v-for="item in countItems" :key="item" class="space-y-3">
        <div v-if="(getFirstNumberValue(item) ?? -1) >= 0" :class="classes.fieldsetContainer">
          <div :class="classes.label">
            {{ t('pages.audience.audienceCreatePage.block.visit.' + item + '.label') }}
          </div>
          <div :class="classes.tagsContainer">
            <BaseChip color="slate" :text="item === 'checkinCount' ? checkinCountTag : averageCheckinCountTag" />
            <div
              v-if="
                rules[item] &&
                isAudienceRuleWithParameters(rules[item]) &&
                isRuleValueValid(rules[item]?.parameters?.date_filter?.value)
              "
              class="flex space-x-2"
            >
              <span class="text-sm font-medium text-foreground">-</span>
              <BaseChip
                color="slate"
                :text="item === 'checkinCount' ? checkinCountDateFilterTag : averageCheckinCountDateFilterTag"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="hasSelectedChannelTags" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.visit.tags.label') }}
        </div>
        <div class="flex flex-wrap gap-3">
          <template v-for="tag in numericTagIds" :key="tag">
            <BaseChip
              v-if="channelTagIds.has(tag)"
              class="text-xs"
              :text="channelTagById.get(tag)?.name || ''"
              :color="channelTagById.get(tag)?.color ?? 'slate'"
            />
          </template>
        </div>
      </div>
    </div>
    <div v-if="hasEngagementBlockWithTags" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.engagement.title') }}
        </div>
      </div>
      <div v-if="hasPointTotalRule" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.engagement.pointTotal.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip color="slate" :text="pointTotalTag" />
        </div>
      </div>
      <div v-if="hasPointCumulatedRule" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.engagement.pointCumulated.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip color="slate" :text="pointCumulatedTag" />
          <div
            v-if="
              rules['pointCumulated'] &&
              isAudienceRuleWithParameters(rules['pointCumulated']) &&
              isRuleValueValid(rules['pointCumulated']?.parameters?.date_filter?.value)
            "
            class="flex space-x-2"
          >
            <span class="text-sm font-medium text-foreground">-</span>
            <BaseChip color="slate" :text="pointCumulatedDateFilterTag" />
          </div>
        </div>
      </div>
      <div v-if="hasSelectedMarketingTags" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.engagement.tags.label') }}
        </div>
        <div class="flex flex-wrap gap-3">
          <template v-for="tag in numericTagIds" :key="tag">
            <BaseChip
              v-if="marketingTagIds.has(tag)"
              :text="marketingTagById.get(tag)?.name || ''"
              :color="marketingTagById.get(tag)?.color ?? 'slate'"
            />
          </template>
        </div>
      </div>
      <div v-if="hasRuleValues('canReceivePushNotifications')" :class="classes.fieldsetContainer">
        <div :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.label') }}
        </div>
        <div :class="classes.tagsContainer">
          <BaseChip
            color="slate"
            :text="
              rules['canReceivePushNotifications']?.value?.[0] === true
                ? t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.yes')
                : t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.no')
            "
          />
        </div>
      </div>
    </div>
    <div v-if="hasContestGameParticipationBlock" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.contestGame.title', games.length > 1 ? 2 : 1) }}
        </div>
      </div>
      <div v-for="contest in games" :key="contest.id" :class="classes.fieldsetContainer">
        <BaseChip :text="contest.name" color="slate" />
      </div>
    </div>
    <div v-if="hasWebappLoginRule" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.webappLogin.title') }}
        </div>
      </div>
      <div :class="classes.fieldsetContainer">
        <div :class="classes.tagsContainer">
          <BaseChip
            v-if="getRuleValues('webappLogIn')[0] === 1"
            :text="t('pages.audience.audienceCreatePage.block.engagement.webappConnection.yes')"
            color="slate"
          />
          <BaseChip
            v-if="getRuleValues('webappLogIn')[0] === 0"
            :text="t('pages.audience.audienceCreatePage.block.engagement.webappConnection.no')"
            color="slate"
          />
        </div>
      </div>
    </div>

    <div v-if="hasSurveyBlock" :class="classes.blockContainer">
      <div :class="classes.blockTitleContainer">
        <div :class="classes.blockTitle">
          {{ t('pages.audience.audienceCreatePage.block.surveys.title', surveys.length > 1 ? 2 : 1) }}
        </div>
      </div>
      <div v-for="survey in surveys" :key="survey.id" ref="surveyBlock" :class="classes.fieldsetContainer">
        <div
          v-if="survey.choices?.some((choice) => getRuleValues('surveys').includes(choice.id))"
          :class="classes.label"
        >
          {{ survey.questionText }}
        </div>
        <div v-if="survey.choices.every((choice) => getRuleValues('surveys').some((rule) => rule === choice.id))">
          <BaseChip :text="t('global.multiselect.surveys.allSelectedText')" color="slate" />
        </div>
        <div
          v-for="choice in survey.choices.filter((choice) =>
            getRuleValues('surveys').some((rule) => rule === choice.id)
          )"
          v-else
          :key="choice.id"
          :class="classes.tagsContainer"
        >
          <BaseChip :text="choice.text" color="slate" />
        </div>
      </div>
      <template v-if="!surveyBlock">
        <AppSkeleton v-for="i in 2" :key="i" type="text" class="mt-3" />
      </template>
    </div>
  </div>
</template>
